import type { V2 } from '../../core/math';
import type { Cost } from '../../game/inventory';

/**
 * Sailing content and tuning (research 02 §6, scaled to one ship): ship
 * levels, the hireable crew, ports, salvage sites and hazard numbers. Pure
 * data — the ship math lives in `ship.ts`, hazards in `hazards.ts`.
 */

/** Actor model used while sailing (12.4 m hull facing +Z). */
export const SHIP_MODEL = 'ship_player';
export const SHIP_NAME = 'The Lanternwake';
export const SHIP_MAX_LEVEL = 10;
/** Collision radius of a ship actor (body-to-body separation). */
export const SHIP_RADIUS = 2.6;
/** Distance from the hull centre to the bow / stern probes used against land. */
export const BOW_REACH = 5;
export const STERN_REACH = 4;
/** The hull model's origin sits this far below the waterline. */
export const SHIP_DRAFT = -0.9;
/** Minimum character level to put to sea (matches the Havenreach gangway). */
export const SAIL_MIN_LEVEL = 15;
/** Metres per second → knots, for the helm readout. */
export const KNOTS = 1.944;

// ---------------------------------------------------------------- ship levels

export interface ShipLevelInfo {
  maxSpeed: number;
  accel: number;
  turnRate: number;
  maxHull: number;
  crewSlots: number;
  /** Fraction of storm damage and push ignored. */
  stormResist: number;
}

/** Base ship stats by level (1..10): steady gains, crew berths at 4/7/10. */
export function shipLevelInfo(level: number): ShipLevelInfo {
  const l = Math.max(1, Math.min(SHIP_MAX_LEVEL, Math.round(level)));
  return {
    maxSpeed: 8.5 + 0.5 * (l - 1),
    accel: 2.6 + 0.12 * (l - 1),
    turnRate: 0.9 + 0.035 * (l - 1),
    maxHull: 100 + 20 * (l - 1),
    crewSlots: l >= 10 ? 5 : l >= 7 ? 4 : l >= 4 ? 3 : 2,
    stormResist: 0.02 * (l - 1),
  };
}

/** Shipwright refit from `level` to `level + 1` (null at max). Timber and ore come from life skills and salvage. */
export function upgradeCost(level: number): Cost | null {
  const l = Math.round(level);
  if (l < 1 || l >= SHIP_MAX_LEVEL) return null;
  const items: Record<string, number> = { timber: 4 + 4 * l };
  if (l >= 2) items.ore_raw = 4 * (l - 1);
  if (l >= 4) items.timber_rare = l - 3;
  if (l >= 6) items.relic_fragment = 2 * (l - 5);
  if (l >= 8) items.ore_rare = l - 7;
  return { silver: Math.round(300 + 100 * l + 60 * l * l), items };
}

/** Silver per missing hull point at a harbour (before crew discounts). */
export const repairRate = (level: number): number => 1.5 + 0.25 * Math.max(1, level);
/** Tug fee when the hull breaks at sea. */
export const towFee = (level: number): number => 100 + 40 * Math.max(1, level);
/** Fraction of max hull the harbour crew patches after a tow. */
export const TOW_HULL = 0.5;
/** Seconds between the hull breaking and the tug arriving. */
export const TOW_DELAY = 2.6;

// ---------------------------------------------------------------- crew

export type CrewBonus = 'speed' | 'turn' | 'hull' | 'storm' | 'fog' | 'repair' | 'salvage' | 'cannon';

export interface SailorDef {
  id: string;
  name: string;
  role: string;
  bonus: CrewBonus;
  /** Bonus magnitude (fraction). */
  value: number;
  /** Silver to sign them on. */
  cost: number;
  blurb: string;
  hue: number;
}

export const SAILORS: readonly SailorDef[] = [
  { id: 'maren', name: 'Maren Tidewell', role: 'Navigator', bonus: 'turn', value: 0.15, cost: 400, hue: 0x7ad8ff, blurb: 'Reads a current the way others read letters from home.' },
  { id: 'pell', name: 'Pell Quickline', role: 'Rigger', bonus: 'speed', value: 0.06, cost: 500, hue: 0x9fe0ff, blurb: 'Can reef a topsail in the time it takes to curse the wind.' },
  { id: 'odrik', name: 'Odrik Stoutkeel', role: 'Bosun', bonus: 'storm', value: 0.2, cost: 450, hue: 0x8aa4ff, blurb: 'Has ridden out eleven gales and names each one.' },
  { id: 'nell', name: 'Nell Oakum', role: 'Carpenter', bonus: 'hull', value: 0.12, cost: 450, hue: 0xd0a060, blurb: 'Sings to the planks. The planks seem to listen.' },
  { id: 'garrow', name: 'Garrow Pitch', role: 'Shipwright', bonus: 'repair', value: 0.25, cost: 350, hue: 0xc08040, blurb: 'Knows every dockside timber merchant and what they owe him.' },
  { id: 'syla', name: 'Syla Farsight', role: 'Lookout', bonus: 'fog', value: 0.45, cost: 300, hue: 0xe0e8f0, blurb: 'Spots a gull in a fog bank. Spots a lie faster.' },
  { id: 'bex', name: 'Bex Tallow', role: 'Quartermaster', bonus: 'salvage', value: 0.25, cost: 400, hue: 0xffd070, blurb: 'Counts twice, trades once, never leaves a crate behind.' },
  { id: 'ivo', name: 'Ivo Brasshand', role: 'Gunner', bonus: 'cannon', value: 0.3, cost: 500, hue: 0xff8a40, blurb: 'Lost two fingers to a bad fuse. Never a third.' },
];

export const SAILOR_BY_ID: Readonly<Record<string, SailorDef>> = Object.fromEntries(SAILORS.map((s) => [s.id, s]));

const pct = (v: number): string => `${Math.round(v * 100)}%`;

/** Human-readable crew bonus. */
export function crewBonusText(s: Pick<SailorDef, 'bonus' | 'value'>): string {
  switch (s.bonus) {
    case 'speed':
      return `Top speed +${pct(s.value)}`;
    case 'turn':
      return `Turn rate +${pct(s.value)}`;
    case 'hull':
      return `Max hull +${pct(s.value)}`;
    case 'storm':
      return `Storm damage & push −${pct(s.value)}`;
    case 'fog':
      return `Sight in fog +${pct(s.value)}`;
    case 'repair':
      return `Repair costs −${pct(s.value)}`;
    case 'salvage':
      return `Salvage silver +${pct(s.value)}`;
    case 'cannon':
      return `Cannon cooldown −${pct(s.value)}`;
  }
}

// ---------------------------------------------------------------- ports

export interface PortInfo {
  id: string;
  name: string;
  /** Sea spawn a ship leaves from. */
  seaSpawn: string;
}

/** Harbours with a pier on the Gleaming Sea (zone id → info). */
export const PORTS: Readonly<Record<string, PortInfo>> = {
  havenreach: { id: 'havenreach', name: 'Havenreach Harbour', seaSpawn: 'fromHavenreach' },
  haven: { id: 'haven', name: 'Haven Isle', seaSpawn: 'fromHaven' },
  glimmer: { id: 'glimmer', name: 'Glimmer Isle', seaSpawn: 'fromGlimmer' },
};

export const SEA_ZONE = 'sea';
/** Spawn used when docking at (or being towed to) a port. */
export const PORT_ARRIVAL = 'fromSea';
/** Character flag remembering the last harbour (tow destination). */
export const LAST_PORT_FLAG = 'sailLastPort';

export const isPort = (zoneId: string | null | undefined): zoneId is string => !!zoneId && zoneId in PORTS;

// ---------------------------------------------------------------- hazards & wear

/** Hull lost per second in a storm's eye (edges deal 40%). */
export const STORM_DPS = 3.2;
/** Strongest storm current (m/s): a swirl plus an outward shove. */
export const STORM_WIND = 3.4;
/** Hull lost to a lightning strike that hits the ship. */
export const LIGHTNING_DAMAGE = 9;
/** Strikes within this distance of the hull centre hit the ship. */
export const LIGHTNING_HIT_RADIUS = 5;
/** Hull lost per metre of open water sailed (general wear). */
export const WEAR_PER_METRE = 1 / 160;
/** Running aground faster than this (m/s) damages the hull. */
export const BUMP_SPEED = 4.5;
export const BUMP_DAMAGE = 1.6;
/** Fraction of max hull Patch the Hull restores over its duration. */
export const PATCH_FRACTION = 0.2;

// ---------------------------------------------------------------- salvage

export type WreckKind = 'flotsam' | 'derelict';

export interface WreckSite extends V2 {
  id: string;
  kind: WreckKind;
  name: string;
}

/**
 * Salvage sites. Flotsam drifts in open water; derelicts lie in storm eyes
 * and fog banks (risk for reward). Everyone sees the same sites; salvage is
 * personal and each site respawns per character.
 */
export const WRECK_SITES: readonly WreckSite[] = [
  { id: 'wreck_0', kind: 'flotsam', name: 'Drifting Flotsam', x: -95, z: 175 },
  { id: 'wreck_1', kind: 'flotsam', name: 'Drifting Flotsam', x: 75, z: 30 },
  { id: 'wreck_2', kind: 'derelict', name: 'Storm-Wracked Derelict', x: -5, z: 62 },
  { id: 'wreck_3', kind: 'flotsam', name: 'Drifting Flotsam', x: -40, z: -80 },
  { id: 'wreck_4', kind: 'derelict', name: 'Fogbound Derelict', x: -195, z: 25 },
  { id: 'wreck_5', kind: 'flotsam', name: 'Drifting Flotsam', x: 130, z: -150 },
  { id: 'wreck_6', kind: 'derelict', name: 'Fogbound Derelict', x: 158, z: 142 },
  { id: 'wreck_7', kind: 'flotsam', name: 'Drifting Flotsam', x: -235, z: -80 },
  { id: 'wreck_8', kind: 'flotsam', name: 'Drifting Flotsam', x: 215, z: 90 },
  { id: 'wreck_9', kind: 'derelict', name: 'Storm-Wracked Derelict', x: 48, z: -122 },
];

/** Sailing within this distance of a wreck salvages it. */
export const SALVAGE_RANGE = 7;
/** A cannonball passing this close to a wreck knocks its cargo loose. */
export const CANNON_BLAST_RADIUS = 3.5;
/** Real-time respawn per character. */
export const WRECK_RESPAWN_MS: Readonly<Record<WreckKind, number>> = { flotsam: 5 * 60_000, derelict: 15 * 60_000 };
/** Hull the crew patches from flotsam timber. */
export const FLOTSAM_HULL = 6;

// ---------------------------------------------------------------- ship abilities

export const FULL_SAIL_STATUS = 'ship_full_sail';
export const PATCH_STATUS = 'ship_patch';
export const SKILL_FULL_SAIL = 'ship_full_sail';
export const SKILL_CANNON = 'ship_cannon';
export const SKILL_PATCH = 'ship_patch';
/** Full Sail speed multiplier. */
export const FULL_SAIL_SPEED = 1.5;
export const CANNON_RANGE = 30;
export const CANNON_SPEED = 34;
